<script lang="ts" setup>
// -------------------------------
// 🔹 Imports Vue & composables
// -------------------------------
import { form, isLoaderLoginFormActivated } from '../../refs/login.form.refs';

// -------------------------------
// 🔹 Composants
// -------------------------------
import TextInput from '../common/BaseInput.vue';
import BaseButtonPrimary from '../common/BaseButtonPrimary.vue';

// -------------------------------
// 🔹 Mutations / services
// -------------------------------
import { CommerceMutation } from '@/mutations/commerce.mutations';
const { handleCreateCommerceContact, handleUpdateCommerceContact } =
  CommerceMutation();

// -------------------------------
// 🔹 Imports stores Pinia
// -------------------------------
import { useCommerceStore } from '@/store/commerce.store';
import { useDialogStore } from '@/store/dialog.store';

// -------------------------------
// 🔹 Imports assets / refs / données
// -------------------------------
import {
  idToDelete,
  isStepTwoActivated,
  previewUrl,
  isStepThreeActivated,
  file,
  form_CommerceBody,
  idToAddValue,
  isUpdatedFromConatct,
  form_CommerceContactBody,
  isAtcivatedWatchContact,
} from '@/refs/commerce.refs';
import { content, isGlobalError, restaurantId } from '@/refs/common.refs';
import { useQuery } from '@tanstack/vue-query';

// -------------------------------
// 🔹 Stores instanciés
// -------------------------------
const commerceStore = useCommerceStore();
const dialogStore = useDialogStore();

const {
  data: dataGetCommerces,
  isLoading: isLoadingGetCommerces,
  isError: isErrorGetCommerces,
  refetch: refectGetCommerces,
} = useQuery({
  queryKey: ['GetCommerces'],
  queryFn: () => commerceStore.getCommerces(),
});

// -------------------------------
// 🔹 Émission d’événements
// -------------------------------
const emit = defineEmits<{
  (e: 'HandleClose'): void;
}>();

// -------------------------------
// 🔹 Gestion du formulaire
// -------------------------------

/**
 * Soumet le formulaire de création de commerce.
 * Gère les différentes étapes : création du commerce, ajout du contact, ou upload du logo.
 */
const handleCreateHorraireCommerceFunc = () => {
  try {
    isLoaderLoginFormActivated.value = true;
    const resp = handleCreateCommerceContact({
      type: form_CommerceContactBody.type,
      content: form_CommerceContactBody.content,
    });

    if (resp) {
      setTimeout(() => {
        isAtcivatedWatchContact.value = !isAtcivatedWatchContact.value;
        isLoaderLoginFormActivated.value = false;
        dialogStore.closeDialog();
      }, 1000);
    }
  } catch (error) {
    isGlobalError.value = true;
    setTimeout(() => {
      isGlobalError.value = false;
      isLoaderLoginFormActivated.value = false;
      dialogStore.closeDialog();
    }, 2000);
  }
};

const handleUpdateHorraireContact = () => {
  try {
    isLoaderLoginFormActivated.value = true;
    const resp = handleUpdateCommerceContact({
      content: form_CommerceContactBody.content,
    });

    if (resp) {
      setTimeout(() => {
        isAtcivatedWatchContact.value = !isAtcivatedWatchContact.value;
        isLoaderLoginFormActivated.value = false;
        dialogStore.closeDialog();
      }, 1000);
    }
  } catch (error) {
    isGlobalError.value = true;
    setTimeout(() => {
      isGlobalError.value = false;
      isLoaderLoginFormActivated.value = false;
      dialogStore.closeDialog();
    }, 2000);
  }
};

const handleSubmit = async () => {
  if (isUpdatedFromConatct.value) {
    handleUpdateHorraireContact();
  } else {
    handleCreateHorraireCommerceFunc();
  }
};

/**
 * Ferme le formulaire et réinitialise l’ID à supprimer.
 */
const handleClose = (): void => {
  idToDelete.value = null;
  emit('HandleClose');
};
</script>

<template>
  <form
    autocomplete="on"
    accept-charset="UTF-8"
    @submit.prevent="handleSubmit"
    class="space-y-6"
  >
    <div class="space-y-[16px]">
      <div v-show="!isUpdatedFromConatct">
        <label for="type" class="mb-1 block font-medium text-gray-700">
          Sélectionnez le type de contact<span
            class="text-decroissance text-14px leadinspanrmal"
            >*</span
          >
        </label>

        <select
          :required="isUpdatedFromConatct ? false : true"
          v-model="form_CommerceContactBody.type"
          id="type"
          name="type"
          class="border-border-input w-full rounded-md border border-solid px-3 py-2 focus-within:border focus-within:border-solid focus:outline-none"
        >
          <option disabled value="">Sélectionnez un type</option>
          <option value="email">Email</option>
          <option value="phone">Téléphone</option>
          <option value="twitter">Twitter</option>
          <option value="facebook">Facebook</option>
        </select>
      </div>

      <TextInput
        required="true"
        v-model="form_CommerceContactBody.content"
        name="Entrez la valeur du contact"
        placeholder="Entrez la valeur du contact si Téléphone ,commencez par l'indicatif"
        type="text"
        id="Entrez la valeur du contact"
        label="Entrez la valeur du contact"
      />

      <div
        class="flex w-full flex-1 flex-col gap-2"
        v-show="!isUpdatedFromConatct"
      >
        <label for="commerce" class="mb-1 block font-medium text-gray-700">
          Sélectionnez le commerce<span
            class="text-decroissance text-14px leadinspanrmal"
            >*</span
          >
        </label>
        <select
          id="commerce"
          name="commerce_associé"
          v-model="idToAddValue"
          :required="isUpdatedFromConatct ? false : true"
          class="border-border-input focus:ring-primary-900 focus:border-primary-900 w-full rounded-xl border bg-white px-3 py-2 text-sm text-gray-700 placeholder-gray-400 shadow-sm transition-all outline-none hover:border-gray-400 focus:ring-2"
        >
          <option value="" disabled>Veuillez sélectionner un commerce</option>
          <option
            v-for="value in dataGetCommerces?.data"
            :value="value?.id"
            class="text-14px text-secondary-text"
          >
            {{ value.designation }}
          </option>
        </select>
      </div>
    </div>

    <div class="invisible h-[50px]" />

    <div class="fixed bottom-0 left-0 w-full bg-white py-6">
      <div class="flex flex-row items-center justify-between gap-8 px-[16px]">
        <div class="w-full">
          <BaseButtonPrimary
            :isClicked="isLoaderLoginFormActivated"
            type="submit"
            :label="isUpdatedFromConatct ? 'Modifier' : 'Enregistrer'"
            style_of_btn="primary_color_of_btn "
            class="w-full text-white"
          />
        </div>

        <div class="w-full" @click="handleClose">
          <BaseButtonPrimary
            type="button"
            :label="'Annuler'"
            style_of_btn="secondary_color_of_btn "
            class="text-grand-text w-full"
          />
        </div>
      </div>
    </div>
  </form>
</template>
